import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import TierBadge from '../components/TierBadge.jsx'
import { directoryNote, useDirectory } from '../directory.js'
import { loadBoard, setInvitees, setVisibility } from '../trips/api.js'
import {
  CREW,
  INVITEES,
  delta,
  departedNote,
  hasChanges,
  offerable,
  onTrip,
  pendingHeadcount,
  visibilityLine,
} from '../trips/people.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// Who is on this trip — P6-81, and Jordan's ask in one screen: *"the invite
// flow, site version with the member picker, as long as I can choose from all
// the server members."*
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHAT THIS SCREEN EXISTS TO FIX, AND IT IS NOT WHAT THE BOARD ROW SAID
// ═══════════════════════════════════════════════════════════════════════════
//
// The row said `visibility: 'invitees'` was *"unreachable from the product"*.
// Measured against the real service, it is the visibility EVERY trip started on
// this site already has — `functions/api/trips/index.js` forwards it, and the
// create form sends no invitees — so every one of them is scoped to a list that
// nothing could fill. `role_for` gives everybody else NONE, which means **every
// site-made trip has been visible to its organiser and to nobody else.**
//
// `src/routes/Trips.jsx` said the opposite in a comment ("a trip with none is a
// crew trip the whole server can see"). The comment was the thing that was
// wrong, and it is now corrected there rather than here.
//
// So this screen answers the two questions that state raises, together, because
// to a person they are one question:
//
//   * **who is on the trip** — the delta, the picker, and the headcount that
//     every "≈ each" figure on the board divides by;
//   * **who can see it** — `crew` or `invitees`, which was reachable from no
//     surface at all before this one.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NO LIVE CHANNEL HERE, AND THAT IS A DECISION RATHER THAN AN OMISSION
// ═══════════════════════════════════════════════════════════════════════════
//
// The board and the dates screen both tail the changes feed, because both are
// surfaces six people edit at once. This one is not: `trips/utils/access.py`
// makes the trip row the ORGANISER's, so the only person who can change
// anything here is the person looking at it. Tailing would buy no liveness and
// cost a real hazard — a background re-read landing mid-pick would reshuffle
// the list somebody is halfway through ticking.
//
// ⚠️ Which is not the same as claiming there is only ever one writer: the same
// organiser can have two tabs. That case is handled where it has to be, in the
// SERVICE, by applying a delta rather than a list — see `src/trips/people.js`
// and `trips/routes/trips.py`. A second session's invite survives this one's
// save, which a whole-list write would have silently undone.
//
// ⚠️ It reads through `loadBoard` rather than a lighter endpoint of its own,
// deliberately: the trip, the actor and the role all arrive in one answer that
// already exists and is already tested, and a second read endpoint would be a
// second place the access model is expressed. The entries and estimate that
// come with it are unused here and cost one request either way.

const EMPTY = new Set()

export default function TripPeople({ section }) {
  const { tripId } = useParams()
  useDocumentTitle('Who is on this trip')

  // 🔴 `useDirectory` CANNOT REJECT (src/directory.js), so there is no `catch`
  // here and no `Promise.all` with the board read. Its three states — loading,
  // ready, unavailable — are rendered as three different sentences, because for
  // the first moment of every page load nothing has failed.
  const directory = useDirectory()

  const [state, setState] = useState({ status: 'loading', data: null, error: null })
  const [adding, setAdding] = useState(EMPTY)
  const [removing, setRemoving] = useState(EMPTY)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [saved, setSaved] = useState(null)

  const refresh = useCallback(async () => {
    const data = await loadBoard(tripId)
    setState({ status: 'ready', data, error: null })
    return data
  }, [tripId])

  useEffect(() => {
    let cancelled = false
    loadBoard(tripId).then(
      (data) => {
        if (!cancelled) setState({ status: 'ready', data, error: null })
      },
      (error) => {
        if (!cancelled) setState({ status: 'error', data: null, error: error.message })
      },
    )
    return () => {
      cancelled = true
    }
  }, [tripId])

  const run = useCallback(
    async (work) => {
      setBusy(true)
      setActionError(null)
      try {
        await work()
        await refresh()
        return true
      } catch (error) {
        setActionError(error.message)
        return false
      } finally {
        setBusy(false)
      }
    },
    [refresh],
  )

  // 🔴 THE HEADING IS RENDERED IN EVERY STATE, loading and failure included —
  // TripDates.jsx's rule, for its two reasons: one h1 per page even before a
  // fetch resolves, and a test asking "which page did the router choose?" needs
  // something to read on the failure path (CLAUDE.md gotcha 21).
  const header = (title) => (
    <header className="d2d-titleblock">
      <p className="d2d-eyebrow d2d-micro">
        <TierBadge tier={section?.tier ?? 3} />
      </p>
      <p className="d2d-board__back">
        <Link to={`/trips/${tripId}`}>← {title ?? 'the trip'}</Link>
      </p>
      <h1 className="d2d-titleblock__headline">Who is on this trip</h1>
    </header>
  )

  if (state.status === 'loading') {
    return (
      <article className="d2d-page d2d-people">
        {header()}
        <hr className="d2d-rule" />
        <p className="d2d-notice" data-testid="people-loading">
          Loading the trip.
        </p>
      </article>
    )
  }

  if (state.status === 'error') {
    return (
      <article className="d2d-page d2d-people">
        {header()}
        <hr className="d2d-rule" />
        <p className="d2d-notice" data-testid="people-error">
          {state.error}
        </p>
      </article>
    )
  }

  const { trip, you } = state.data
  const organiser = trip.your_role === 'organiser'
  const context = { directory, you, organiserId: trip.organiser_id }

  const rows = onTrip(trip, context)
  const candidates = offerable(trip, context)
  const changed = hasChanges(adding, removing)
  const preview = pendingHeadcount(trip, adding, removing)
  const note = directoryNote(directory)
  const departed = departedNote(rows)

  const toggle = (setter, id) =>
    setter((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const save = async () => {
    setSaved(null)
    const asked = delta(adding, removing)
    const ok = await run(() => setInvitees(tripId, asked))
    if (ok) {
      setAdding(EMPTY)
      setRemoving(EMPTY)
      setSaved(asked)
    }
  }

  return (
    <article className="d2d-page d2d-people">
      {header(trip.title)}
      <hr className="d2d-rule" />

      {actionError ? (
        <p className="d2d-notice d2d-notice--warn" role="status" data-testid="people-action-error">
          {actionError}
        </p>
      ) : null}

      {/* ── who can see it ──────────────────────────────────────────────── */}
      <section className="d2d-people__visibility" aria-label="Who can see this trip">
        <h2 className="d2d-people__h2">Who can see it</h2>
        <p className="d2d-people__line" data-testid="visibility-line">
          {visibilityLine(trip.visibility, trip.headcount)}
        </p>
        {organiser ? (
          <div className="d2d-people__choices" role="group" aria-label="Who can see this trip">
            {[
              [CREW, 'Anyone in the server'],
              [INVITEES, 'Only the people on the trip'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`d2d-people__choice${
                  trip.visibility === value ? ' d2d-people__choice--on' : ''
                }`}
                aria-pressed={trip.visibility === value}
                disabled={busy || trip.visibility === value}
                data-testid={`visibility-${value}`}
                onClick={() => run(() => setVisibility(tripId, value))}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </section>

      {/* ── who is on it ────────────────────────────────────────────────── */}
      <section className="d2d-people__on" aria-label="On the trip">
        <h2 className="d2d-people__h2">
          On the trip · {trip.headcount} {trip.headcount === 1 ? 'person' : 'people'}
        </h2>
        <ul className="d2d-people__list" data-testid="people-on-trip">
          {rows.map((row) => {
            const going = removing.has(row.id)
            return (
              <li
                key={row.id}
                className={`d2d-person${going ? ' d2d-person--leaving' : ''}`}
                data-testid={`person-${row.id}`}
              >
                <span
                  className="d2d-avatar"
                  aria-hidden="true"
                  style={{ background: `var(${row.token})` }}
                />
                <span className="d2d-person__name" title={row.title}>
                  {row.label}
                  {row.left ? (
                    <span className="d2d-micro d2d-person__left"> · left the server</span>
                  ) : null}
                  {row.isOrganiser ? (
                    <span className="d2d-micro d2d-person__role"> · started it</span>
                  ) : null}
                </span>
                {/* 🔴 THE ORGANISER HAS NO REMOVE CONTROL, because there is
                    nothing behind one: `roster_of` puts them on their own trip
                    unconditionally and the service refuses to take them off it.
                    A button that cannot work is worse than none (gotcha 11's
                    rule, one layer up from a decorator). */}
                {organiser && !row.isOrganiser ? (
                  <button
                    type="button"
                    className="d2d-person__toggle"
                    aria-pressed={going}
                    disabled={busy}
                    data-testid={`remove-${row.id}`}
                    onClick={() => toggle(setRemoving, row.id)}
                  >
                    {going ? 'Keep them' : 'Take off'}
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
        {departed ? (
          <p className="d2d-micro d2d-people__note" data-testid="people-departed-note">
            {departed}
          </p>
        ) : null}
      </section>

      {/* ── who else there is ───────────────────────────────────────────── */}
      {organiser ? (
        <section className="d2d-people__add" aria-label="Add somebody">
          <h2 className="d2d-people__h2">Add somebody</h2>
          {/* 🔴 THREE STATES, AND `loading` SAYS NOTHING. src/directory.js's
              rule: for the first moment of every page load nothing has failed,
              and "names are unavailable" there would be the page reporting an
              outage that has not happened. */}
          {directory.state === 'loading' ? (
            <p className="d2d-notice" data-testid="people-directory-loading">
              Loading everyone in the server.
            </p>
          ) : directory.state === 'unavailable' ? (
            <p className="d2d-notice d2d-notice--warn" data-testid="people-directory-unavailable">
              The list of who is in the server is unavailable right now, so nobody can be added
              yet. Everything else on this page is unaffected.
            </p>
          ) : candidates.length === 0 ? (
            <p className="d2d-notice" data-testid="people-nobody-left">
              Everyone in the server is already on this trip.
            </p>
          ) : (
            <ul className="d2d-people__list" data-testid="people-offerable">
              {candidates.map((row) => {
                const coming = adding.has(row.id)
                return (
                  <li
                    key={row.id}
                    className={`d2d-person${coming ? ' d2d-person--joining' : ''}`}
                    data-testid={`candidate-${row.id}`}
                  >
                    <span
                      className="d2d-avatar"
                      aria-hidden="true"
                      style={{ background: `var(${row.token})` }}
                    />
                    <span className="d2d-person__name" title={row.title}>
                      {row.label}
                    </span>
                    <button
                      type="button"
                      className="d2d-person__toggle"
                      aria-pressed={coming}
                      disabled={busy}
                      data-testid={`add-${row.id}`}
                      onClick={() => toggle(setAdding, row.id)}
                    >
                      {coming ? 'Not them' : 'Add'}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      ) : null}

      {note ? (
        <p className="d2d-micro d2d-people__note" data-testid="people-directory-note">
          {note}
        </p>
      ) : null}

      {organiser ? (
        <div className="d2d-people__footer">
          {/* The consequence of the pending change, in the units the board
              uses. `headcount` is the denominator of every "≈ each" figure, so
              a person changing this list is changing every price on the trip
              and should be told so before they press the button. */}
          <p className="d2d-people__preview" data-testid="people-preview">
            {changed
              ? `${preview} ${preview === 1 ? 'person' : 'people'} on the trip once you save. ` +
                'Costs are split between them.'
              : 'Nothing to save yet.'}
          </p>
          <button
            type="button"
            className="d2d-people__save"
            disabled={busy || !changed}
            data-testid="people-save"
            onClick={save}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      ) : null}

      {saved ? (
        <p className="d2d-notice" role="status" data-testid="people-saved">
          Saved. {saved.add.length > 0 ? `${saved.add.length} added. ` : ''}
          {saved.remove.length > 0 ? `${saved.remove.length} taken off. ` : ''}
          Nobody is told automatically — this is who can see the trip, not an announcement.
        </p>
      ) : null}
    </article>
  )
}
